import { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";

import useGetSessionTrialConfig from "../../hooks/useGetSessionTrialConfig";
import useGetVisits from "../../hooks/useGetVisits";
import useVisitPhotoTypes from "../../hooks/useVisitPhotoTypes";
import useGetSurveys from "../../hooks/useGetSurveys";
import useVisitAssessmentAreas from "../../hooks/useVisitAssessmentAreas";
import PhotosModule from "../TrialAccordions/PhotosModule";
import VideosModule from "../TrialAccordions/VideosModule";
import SurveysModule from "../TrialAccordions/SurveysModule";
import AdvancedSurveysModule from "../TrialAccordions/AdvancedSurveysModule";
import AIAssessmentModule from "../TrialAccordions/AIAssessmentModule";

const ACTIVITIES = [
  { key: "enable_photos", label: "Photos" },
  { key: "enable_videos", label: "Videos" },
  { key: "enable_poem", label: "POEMs" },
  { key: "enable_advanced_survey", label: "ePRO / POEMs / eDiary" },
  { key: "enable_ai_assessment", label: "AI Assessment" },
];

const SessionActivities = ({ trialId, role }) => {
  const { data: config, isLoading: configLoading } = useGetSessionTrialConfig({ trialId });

  const [form, setForm] = useState(null);

  useEffect(() => {
    if (config) {
      setForm({
        enable_photos: config.enable_photos,
        enable_videos: config.enable_videos,
        enable_poem: config.enable_poem,
        enable_advanced_survey: config.enable_advanced_survey,
        enable_ai_assessment: config.enable_ai_assessment,
      });
    }
  }, [config]);

  const masterVisitId = config?.master_visit_id;

  const { data: visits = [] } = useGetVisits({ trialId, enabled: !!masterVisitId });
  const masterVisit = visits.find((v) => v.visit_number === -5) || null;

  const { data: photoTypes = [] } = useVisitPhotoTypes({
    trialId,
    enabled: !!form?.enable_photos,
  });

  const { data: allSurveys = [] } = useGetSurveys({
    enabled: !!form?.enable_poem,
  });

  const { data: assessmentAreas = [] } = useVisitAssessmentAreas({
    visitId: null,
    trialId: trialId,
    enabled: !!form?.enable_ai_assessment,
  });

  const renderModule = (key) => {
    if (!masterVisit) return null;
    const commonProps = {
      role,
      visitId: masterVisitId,
      selectedVisit: masterVisit,
      headerless: true,
    };

    switch (key) {
      case "enable_photos":
        return (
          <PhotosModule
            {...commonProps}
            photoTypes={photoTypes}
            manageTestId="session-activity-photos-manage-btn"
          />
        );
      case "enable_videos":
        return <VideosModule {...commonProps} manageTestId="session-activity-videos-manage-btn" />;
      case "enable_poem":
        return (
          <SurveysModule
            {...commonProps}
            allSurveys={allSurveys}
            manageTestId="session-activity-poem-manage-btn"
          />
        );
      case "enable_advanced_survey":
        return (
          <AdvancedSurveysModule
            {...commonProps}
            manageTestId="session-activity-adv-surveys-manage-btn"
          />
        );
      case "enable_ai_assessment":
        return (
          <AIAssessmentModule
            {...commonProps}
            trial={{ has_ai_evaluation: true }}
            assessmentAreas={assessmentAreas}
            manageTestId="session-activity-ai-assessment-manage-btn"
          />
        );
      default:
        return null;
    }
  };

  if (configLoading || !form) {
    return (
      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="rectangular" height={36} />
        ))}
      </Box>
    );
  }

  const enabledActivities = ACTIVITIES.filter(({ key }) => form[key]);

  if (enabledActivities.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No activities enabled for this trial. Enable activities from the module order settings
          above.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 1 }}>
      {enabledActivities.map(({ key, label }) => (
        <Box key={key}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 0.5,
            }}
          >
            <Typography variant="body1">{label}</Typography>
            {renderModule(key)}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SessionActivities;
